import React from "react";
import { IEventConfig, IEventNFTPrize, IEventRank } from "../../../../helpers/interfaces/event";
import { uuidv4 } from "@firebase/util";
import { log } from "../../../../helpers/utils/log";
import { addMillisecondsToTimestamp } from "../../../../helpers/dates";
import { isEqual } from "lodash";

const rankFactory = (index: number): IEventRank => {
  const ran100 = Math.floor(Math.random() * 100);
  const ran1000 = ran100 * 10;
  const ran10000 = ran1000 * 10;
  return {
    tbUid: uuidv4(),
    rank: index + 1,
    dragons: ran100,
    damageInflicted: ran1000,
    hpLeft: ran100,
    rounds: ran100,
    isPlayer: false,
    score: ran10000,
    nickname: `Random player ${ran100}${ran1000}`
  }
}

export const fakeRanks = (tbUid: string = "7f0907b4-30c2-44ae-b4a2-c08ec84f94e0", customPosition = 0) => {
  const randomCount = 100;
  const result = [];

  customPosition = Math.max(0, Math.min(99, customPosition));

  for (let i = 0; i < randomCount; ++i) {
    const rank = rankFactory(i);
    result.push(rank);
  }

  result[customPosition].tbUid = tbUid;
  result[customPosition].nickname = "You, but with a fake name";

  return result;
}

export enum EEventPhase {
  StartingSoon = "StartingSoon",
  OnGoing = "OnGoing",
  Ended = "Ended",
}

export interface IEventState {
  loading: boolean;
  config: IEventConfig | null;
  ranks: IEventRank[];
  phase: EEventPhase;
  logMessage?: { message: string, color: string, icon?: React.ReactNode };
}

export const eventInitialState: IEventState = {
  loading: true,
  config: null,
  ranks: [],
  phase: EEventPhase.StartingSoon,
  logMessage: { message: "", color: "" }
}

export enum EEventActions {
  LoadConfig = "LoadConfig",
  LoadConfigSuccess = "LoadConfigSuccess",
  LoadConfigFail = "LoadConfigFail",
  RefreshConfig = "RefreshConfig",
  RefreshConfigSuccess = "RefreshConfigSuccess",
  RefreshConfigFail = "RefreshConfigFail",
  LoadRankings = "LoadRankings",
  LoadRankingsSuccess = "LoadRankingsSuccess",
  LoadRankingsFail = "LoadRankingsFail",
  SetPhase = "SetPhase"
}

interface IEventAction {
  type: EEventActions;
  config?: IEventConfig | null;
  ranks?: IEventRank[];
  phase?: EEventPhase;
  logMessage?: { message: string, color: string, icon?: React.ReactElement };
}

function getPhase({ startTimestamp, endTimestamp }: IEventConfig) {
  const now = Date.now();

  const ended = addMillisecondsToTimestamp(endTimestamp) - now <= 0;
  if (ended) {
    return EEventPhase.Ended;
  }

  const started = addMillisecondsToTimestamp(startTimestamp) - now <= 0;
  if (started) {
    return EEventPhase.OnGoing;
  }

  return EEventPhase.StartingSoon;
}


function addExpectedEarningTo(ranks: IEventRank[], config: IEventConfig | null) {
  if (!config) {
    return ranks;
  }

  if (!ranks || !ranks.length) {
    return [];
  }

  if (!config.nftPrizes || !config.nftPrizes.length) {
    return ranks;
  }

  ranks.map((r, i) => {
    r.expectedEarnings = [];
    config.nftPrizes?.forEach((nftPrize: IEventNFTPrize) => {
      r.expectedEarnings!.push(
        {
          image: nftPrize.image,
          name: nftPrize.name,
          amount: nftPrize.distribution[i] || 0
        }
      )
    });
  });

  return ranks;
}


export const eventReducer = (state: IEventState, action: IEventAction): IEventState => {
  const reducer = (state: IEventState, action: IEventAction): IEventState => {
    let ranks = [];
    switch (action.type) {
      case EEventActions.SetPhase:
        return {
          ...state,
          phase: action.phase || state.phase
        };

      case EEventActions.LoadConfig:
        return {
          ...state,
          loading: true,
          ranks: [],
          logMessage: eventInitialState.logMessage
        };

      case EEventActions.RefreshConfig:
        return {
          ...state,
          loading: false,
          logMessage: eventInitialState.logMessage
        };

      case EEventActions.RefreshConfigSuccess:
        if (isEqual(action.config, state.config)) {
          return state;
        }

        return {
          ...state,
          loading: false,
          config: action.config!,
          phase: getPhase(action.config!)
        };

      case EEventActions.LoadConfigSuccess:
        return {
          ...state,
          loading: false,
          config: action.config!,
          phase: getPhase(action.config!)
        };

      case EEventActions.RefreshConfigFail:
      case EEventActions.LoadConfigFail:
        return {
          ...state,
          loading: false,
          logMessage: action.logMessage
        };

      case EEventActions.LoadRankings:
        return {
          ...state,
          logMessage: eventInitialState.logMessage
        };

      case EEventActions.LoadRankingsSuccess:
        ranks = addExpectedEarningTo(action.ranks!, state.config);

        return {
          ...state,
          ranks
        };

      case EEventActions.LoadRankingsFail:
        return {
          ...state,
          logMessage: action.logMessage
        };

      default:
        return state;
    }
  }

  log("EventStore", `Dispatching a new action: `, action);
  const newState = reducer(state, action);
  log("EventStore", `New state: `, newState);

  return newState;
}